import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { scaleIngredients, scaleSteps, formatQuantity, type RecipeIngredient } from '@familyhub/shared';
import { useSession } from '../session';
import { rdb } from './db';
import { RecipeSheet } from './RecipeSheet';
import { AddToListSheet } from './AddToListSheet';
import { logCooked, setPinned, setGrade } from './actions';
import { formatDate } from './datetime';
import { BookIcon, ClockIcon, PotIcon, PinIcon, BasketIcon } from './icons';

/**
 * A single recipe: its picture and time, a servings stepper that live-scales the
 * amounts, the ingredient list, the steps, and the two things you do with a
 * recipe — send its ingredients to the shopping list, and cook it. Reads from the
 * local mirror, so it works with no signal; scaling is view-only and never stored.
 */
export function RecipeScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const session = useSession();
  const userId = session.status === 'in' ? session.userId : null;

  const loaded = useLiveQuery(async () => ({ r: await rdb.recepten.get(id!) }), [id]);
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState(false);
  const [addingToList, setAddingToList] = useState(false);

  if (loaded === undefined) {
    return (
      <div className="app">
        <div className="topbar">
          <Link className="btn btn-quiet back" to="/recepten" aria-label="Terug">‹ Recepten</Link>
          <h1>Recept</h1>
          <span style={{ width: 52 }} aria-hidden />
        </div>
      </div>
    );
  }

  const recipe = loaded.r;
  if (!recipe || recipe.deleted_at) {
    return (
      <div className="app">
        <div className="topbar">
          <Link className="btn btn-quiet back" to="/recepten" aria-label="Terug">‹ Recepten</Link>
          <h1>Recept</h1>
          <span style={{ width: 52 }} aria-hidden />
        </div>
        <p className="agenda-empty">Dit recept bestaat niet meer.</p>
      </div>
    );
  }

  // Servings live in the URL (?porties=), so the chosen amount survives the trip
  // out to cook mode and back — it is view state, never written to the recipe.
  const p = Number(params.get('porties'));
  const servings = Number.isFinite(p) && p > 0 ? Math.min(100, Math.round(p)) : recipe.servings;
  const scaled = scaleIngredients(recipe.ingredients, recipe.servings, servings);
  const scaledSteps = scaleSteps(recipe.steps, recipe.ingredients, recipe.servings, servings);
  const setServings = (n: number) => {
    const next = new URLSearchParams(params);
    next.set('porties', String(Math.min(100, Math.max(1, n))));
    setParams(next, { replace: true });
  };

  // Preserve the ingredient order but break into the recipe's own sections.
  const groups = groupIngredients(scaled);

  const cooked = recipe.cooked_log?.length ?? 0;
  const lastCooked = cooked > 0 ? recipe.cooked_log![cooked - 1]!.at : null;

  return (
    <div className="app">
      <div className="topbar">
        <Link className="btn btn-quiet back" to="/recepten" aria-label="Terug">‹ Recepten</Link>
        {/* Orientation while scrolling; the real, announced title is the <h1>
            in the content below (this one only ever clips a long name). */}
        <div className="recipe-topbar-title" aria-hidden>{recipe.title}</div>
        <button className="btn btn-quiet" onClick={() => setEditing(true)}>Bewerken</button>
      </div>

      <main className="recipe-view">
        {recipe.image_path && (
          <img className="recipe-hero" src={recipe.image_path} alt="" />
        )}

        <header className="recipe-head">
          <div className="recipe-head-top">
            {/* The full name, wrapping — the top-bar copy is clipped to one line,
                so a long recipe title is only ever read in full here. */}
            <h1 className="recipe-title">{recipe.title}</h1>
            {/* Pin is a persistent property of the recipe, not a cook action —
                so it sits with the title as a quiet toggle, green-wash when on. */}
            <button
              className="recipe-fav"
              aria-pressed={!!recipe.pinned}
              onClick={() => setPinned(recipe.id, !recipe.pinned)}
            >
              <PinIcon size={16} /> {recipe.pinned ? 'Losmaken' : 'Vastzetten'}
            </button>
          </div>

          <div className="recipe-meta">
            {recipe.source_name?.trim() && (
              <span className="recipe-meta-item"><BookIcon /> {recipe.source_name.trim()}</span>
            )}
            {recipe.total_minutes != null && (
              <span className="recipe-meta-item"><ClockIcon /> <span className="num">{recipe.total_minutes}</span> min</span>
            )}
            {recipe.tags.map((t) => (
              <span key={t} className="recipe-tag">{t}</span>
            ))}
          </div>

          {/* Status and the one-tap "we cooked this" log, kept together: the
              button acts on exactly the line beside it. */}
          <div className="recipe-status">
            <p className="recipe-cook-stats">
              {cooked > 0 ? (
                <span className="recipe-cook-count"><PotIcon /> <strong>{cooked}×</strong> gekookt{lastCooked && <> · laatst {formatDate(lastCooked)}</>}</span>
              ) : (
                <span className="recipe-cook-count recipe-cook-never"><PotIcon /> Nog niet gekookt</span>
              )}
              {recipe.added_at && <span className="recipe-cook-added">Toegevoegd {formatDate(recipe.added_at)}</span>}
            </p>
            <button className="btn recipe-log-btn" onClick={() => logCooked(recipe.id, userId)}>Gekookt vandaag</button>
          </div>

          {/* The household's one cijfer: ten even keys, tap the current one again
              to clear it. */}
          <div className="recipe-grades">
            <span className="recipe-grades-label">Cijfer</span>
            <div className="recipe-grade-row" role="group" aria-label="Cijfer">
              {Array.from({ length: 10 }, (_, i) => i + 1).map((g) => (
                <button
                  key={g}
                  className="recipe-grade"
                  aria-pressed={recipe.grade === g}
                  aria-label={recipe.grade === g ? `Cijfer ${g} wissen` : `Cijfer ${g} geven`}
                  onClick={() => setGrade(recipe.id, recipe.grade === g ? null : g)}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </header>

        {recipe.ingredients.length > 0 && (
          <section className="recipe-section">
            {/* The servings stepper controls these amounts, so it lives on the
                ingredient heading rather than as a band of its own up top. */}
            <div className="recipe-section-head">
              <h2 className="group-head"><span>Ingrediënten</span></h2>
              <div className="stepper stepper-sm" role="group" aria-label="Aantal porties">
                <button className="btn btn-quiet" aria-label="Minder porties" onClick={() => setServings(servings - 1)} disabled={servings <= 1}>−</button>
                <span className="stepper-value">{servings} {servings === 1 ? 'portie' : 'porties'}</span>
                <button className="btn btn-quiet" aria-label="Meer porties" onClick={() => setServings(servings + 1)}>+</button>
              </div>
            </div>
            <div className="card">
              {groups.map((g) => (
                <div key={g.name ?? '_'}>
                  {g.name && <div className="ing-group">{g.name}</div>}
                  <ul className="ing-list">
                    {g.items.map((ing, i) => (
                      <li key={i} className="ing-row">
                        <span className="ing-amt">{amountLabel(ing)}</span>
                        <span className="ing-name">{ing.name}{ing.note ? ` (${ing.note})` : ''}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {/* Add-to-list belongs to the ingredients — the groceries seam,
                  attached to the thing it acts on, not stacked over the CTA. */}
              <button className="ing-add-row" onClick={() => setAddingToList(true)}>
                <span className="ing-add-label"><BasketIcon /> Naar boodschappenlijst</span>
                <span className="ing-add-chev" aria-hidden>›</span>
              </button>
            </div>
          </section>
        )}

        {recipe.steps.length > 0 && (
          <section className="recipe-section">
            <h2 className="group-head"><span>Bereiding</span></h2>
            <ol className="step-list">
              {scaledSteps.map((s, i) => (
                <li key={i} className="step-row">
                  <span className="step-num">{i + 1}</span>
                  <span className="step-text">{s.text}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {recipe.notes && (
          <section className="recipe-section">
            <h2 className="group-head"><span>Notities</span></h2>
            <p className="recipe-notes">{recipe.notes}</p>
          </section>
        )}
      </main>

      {recipe.steps.length > 0 && (
        <div className="recipe-cta">
          <button
            className="btn btn-primary btn-lg"
            onClick={() => navigate(`/recepten/${recipe.id}/koken?porties=${servings}`)}
          >
            Kookstand starten
          </button>
        </div>
      )}

      {editing && (
        <RecipeSheet
          recipe={recipe}
          userId={userId}
          onClose={() => setEditing(false)}
          onDeleted={() => navigate('/recepten')}
        />
      )}

      {addingToList && (
        <AddToListSheet
          title={recipe.title}
          ingredients={scaled}
          onClose={() => setAddingToList(false)}
        />
      )}
    </div>
  );
}

function amountLabel(ing: RecipeIngredient): string {
  if (ing.quantity == null) return '';
  return ing.unit ? `${formatQuantity(ing.quantity)} ${ing.unit}` : formatQuantity(ing.quantity);
}

interface IngredientGroup {
  name: string | null;
  items: RecipeIngredient[];
}

/** Splits the ingredient list into its sections, preserving order. */
function groupIngredients(ingredients: RecipeIngredient[]): IngredientGroup[] {
  const groups: IngredientGroup[] = [];
  for (const ing of ingredients) {
    const name = ing.group ?? null;
    const last = groups[groups.length - 1];
    if (last && last.name === name) last.items.push(ing);
    else groups.push({ name, items: [ing] });
  }
  return groups;
}
